import * as signalR from "@microsoft/signalr"
import { EventBus } from "@/eventBus"

interface PendingOperation {
	operationId: string
	operationType: string
	resolve: (value: unknown) => void
	reject: (reason: Error) => void
	timeoutId?: number
}

// Module-level singleton state
let connection: signalR.HubConnection | null = null
let isConnected = false
let isConnecting = false
const pendingOperations = new Map<string, PendingOperation>()

export function useDbOperationNotifier() {
	const VITE_API_URL = import.meta.env.VITE_API_URL?.toString() || ""

	async function ensureConnected(): Promise<void> {
		if (isConnected && connection) return
		if (isConnecting) {
			// Wait for the existing connection attempt to complete
			while (isConnecting) {
				await new Promise((resolve) => setTimeout(resolve, 50))
			}
			return
		}

		isConnecting = true

		try {
			connection = new signalR.HubConnectionBuilder()
				.withUrl(VITE_API_URL + "/realtimemessaging")
				.withAutomaticReconnect()
				.configureLogging(signalR.LogLevel.Warning)
				.build()

			connection.on("DbOperationStarted", (operationId: string, operationType: string) => {
				console.log(`[DbOperationNotifier] Operation started: ${operationId} (${operationType})`)
				EventBus.emit("DbOperation:Started", { operationId, operationType })
			})

			connection.on("DbOperationCompleted", (operationId: string, success: boolean, data: unknown) => {
				console.log(`[DbOperationNotifier] Operation completed: ${operationId}, success: ${success}`)
				const pending = pendingOperations.get(operationId)
				if (pending) {
					if (pending.timeoutId) clearTimeout(pending.timeoutId)
					pendingOperations.delete(operationId)
					if (success) {
						pending.resolve(data)
					} else {
						pending.reject(new Error("Operation failed"))
					}
				}
				EventBus.emit("DbOperation:Completed", { operationId, success, data })
			})

			connection.onreconnecting(() => {
				console.log("[DbOperationNotifier] Reconnecting...")
				isConnected = false
			})

			connection.onreconnected(() => {
				console.log("[DbOperationNotifier] Reconnected")
				isConnected = true
			})

			connection.onclose(() => {
				console.log("[DbOperationNotifier] Connection closed")
				isConnected = false
			})

			await connection.start()
			isConnected = true
			console.log("[DbOperationNotifier] Connected to SignalR hub")
		} finally {
			isConnecting = false
		}
	}

	function waitForOperation<T = unknown>(operationId: string, timeout: number = 30000): Promise<T> {
		return new Promise((resolve, reject) => {
			const timeoutId = window.setTimeout(() => {
				pendingOperations.delete(operationId)
				reject(new Error(`Operation ${operationId} timed out after ${timeout}ms`))
			}, timeout)

			pendingOperations.set(operationId, {
				operationId,
				operationType: "",
				resolve: resolve as (value: unknown) => void,
				reject,
				timeoutId,
			})
		})
	}

	function isOperationPending(operationId: string): boolean {
		return pendingOperations.has(operationId)
	}

	function cancelOperation(operationId: string): void {
		const pending = pendingOperations.get(operationId)
		if (pending) {
			if (pending.timeoutId) clearTimeout(pending.timeoutId)
			pendingOperations.delete(operationId)
			console.log(`[DbOperationNotifier] Operation cancelled: ${operationId}`)
		}
	}

	function getPendingOperationsCount(): number {
		return pendingOperations.size
	}

	function getConnection(): signalR.HubConnection | null {
		return connection
	}

	return {
		ensureConnected,
		waitForOperation,
		isOperationPending,
		cancelOperation,
		getPendingOperationsCount,
		getConnection,
	}
}
